/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {Box, makeStyles, Typography} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {Skeleton} from '@ion/dls/src/components/skeleton/Skeleton';
import CheckmarkFilledIcon from '@ion/dls/src/icons/CheckmarkFilledIcon';
import TimeOutlinedIcon from '@ion/dls/src/icons/TimeOutlinedIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {serviceEnabledDateFormat} from './helpers';

interface CarbTabInfoProps {
  status?: string;
  serviceEnabledDate?: string;
  isDeviceLoading?: boolean;
};

export const style = {
  fontFamily    : 'Roboto !important',
  fontWeight    : 500,
  letterSpacing : '0px',
  verticalAlign : 'middle'
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    carbContentStyle : {
      display       : 'flex',
      flexDirection : 'column',
      alignItems    : 'flex-start'
    },
    carbDetailBoxStyles : {
      padding         : '5px 10px',
      height          : '68px',
      minWidth        : '200px',
      backgroundColor : dlsColor('grey6'),
      borderRadius    : '4px',
      display         : 'flex',
      flexDirection   : 'column',
      justifyContent  : 'center',
      alignItems      : 'flex-start',
      marginRight     : '4px',
      marginBottom    : '4px'
    },
    carbIconStyle : {
      display    : 'flex',
      alignItems : 'center'
    },
    carbTitleInfoStyle : {
      ...style,
      color      : dlsColor('grey1'),
      fontSize   : '16px !important',
      lineHeight : '20px'
    },
    carbContentInfoStyle : {
      ...style,
      color      : dlsColor('grey3'),
      fontSize   : '16px !important',
      lineHeight : '20px'
    },
    skeletonSpace : {
      height : 10
    }
  };
});

/**
 * Display the CARB information related to Status, `Initial CARB Service` date and 
 * `Successful update to CARB Service` date.
 * 
 * ```typescript
 * 
 * <CarbTabInfo
 *   status='Enabled'
 *   serviceEnabledDate={date}
 *   isDeviceLoading={isDeviceLoading}
 * />
 * 
 * ```
 */
const CarbTabInfo = ({
  status,
  serviceEnabledDate,
  isDeviceLoading = false
}: CarbTabInfoProps) => {
  const classes = useStyles();
  const path    = 'admin-devices:view.carb.tabInfo';

  return (
    <Box sx={{display: 'flex', flexWrap: 'wrap'}}>
      <div className={classes.carbContentStyle}>
        <Box className={classes.carbDetailBoxStyles}>
          <Typography className={classes.carbTitleInfoStyle}>
            {translate(`${path}.status`)}:
          </Typography>
          <div className={classes.carbIconStyle}>
            <span>
              <CheckmarkFilledIcon size={IconSize.medium} color={dlsColor('caGreen')} />
            </span>
            <Typography className={classes.carbContentInfoStyle}>
              {status|| ''}
            </Typography>
          </div>
        </Box>
      </div>
      <div className={classes.carbContentStyle}>
        <Box className={classes.carbDetailBoxStyles}>
          <Render>
            <Render.If condition={isDeviceLoading}>
              <Skeleton variant='rect' animation='wave' width={140} height={10} />
              <div className={classes.skeletonSpace} />
              <Skeleton variant='rect' animation='wave' width={180} height={10} />
            </Render.If>
            <Render.Else>
              <Typography className={classes.carbTitleInfoStyle}>
                {translate(`${path}.serviceEnabledDate`)}:
              </Typography>
              <div className={classes.carbIconStyle}>
                <span>
                  <TimeOutlinedIcon size={IconSize.medium} color={dlsColor('grey3')} />
                </span>
                <Typography className={classes.carbContentInfoStyle}>
                  {serviceEnabledDate ? serviceEnabledDateFormat(`${serviceEnabledDate}Z`, true) : ''}
                </Typography>
              </div>
            </Render.Else>
          </Render>
        </Box>
      </div>
    </Box>
  );
};

export default CarbTabInfo;
